import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import Navbar from '../../components/headerSection/navbar/Navbar';
import Footer from '../../components/footer/Footer';

// Backend URL aus Environment Variable oder Fallback zu relativer URL
const BACKEND_URL = import.meta.env.VITE_CV_ANONYMIZER_BACKEND || '';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in Bytes

function CVAnonymizer() {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  
  // ========================================
  // STRUCTURED DATA / SCHEMA MARKUP
  // ========================================
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Lebenslauf Anonymisieren | CV Schwärzen | Kostenlos | Jobdaten",
    "description": "Anonymisiere deinen Lebenslauf kostenlos mit KI. Schwärze automatisch Namen, Adressen & Kontaktdaten. Keine Registrierung nötig.",
    "url": "https://jobdaten.com/cv-anonymizer",
    "inLanguage": "de-DE",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Jobdaten",
      "url": "https://jobdaten.com"
    },
    "about": {
      "@type": "SoftwareApplication",
      "name": "CV-Anonymizer",
      "applicationCategory": "ProductivityApplication",
      "description": "Kostenloser KI-gestützter Lebenslauf-Anonymisierer. Schwärzt automatisch persönliche Daten.",
      "operatingSystem": "Web",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "EUR",
        "description": "Kostenlos nutzbar"
      }
    },
    "provider": {
      "@type": "Organization",
      "name": "Diligent Recruitment GmbH",
      "url": "https://jobdaten.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Faberstrasse 11",
        "addressLocality": "Rheinstetten",
        "postalCode": "76287",
        "addressCountry": "DE"
      },
      "telephone": "+49 721 75401610"
    }
  };

  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [downloadLink, setDownloadLink] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const validateFile = (selectedFile) => {
    // Prüfe ob es eine PDF ist
    if (selectedFile.type !== 'application/pdf') {
      setError('❌ Nur PDF-Dateien sind erlaubt. Bitte wähle eine PDF-Datei aus.');
      return false;
    }

    // Prüfe Dateigröße (10MB Limit)
    if (selectedFile.size > MAX_FILE_SIZE) {
      const sizeMB = (selectedFile.size / 1024 / 1024).toFixed(2);
      setError(`❌ Datei zu groß (${sizeMB} MB). Maximale Dateigröße: 10 MB. Bitte komprimiere deine PDF oder wähle eine kleinere Datei.`);
      return false;
    }

    return true;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && validateFile(selectedFile)) {
      setFile(selectedFile);
      setError(null);
      setSuccess(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile && validateFile(droppedFile)) {
      setFile(droppedFile);
      setError(null);
      setSuccess(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleGenerateClick = async () => {
    if (!file) {
      setError('⚠️ Bitte wähle zuerst eine PDF-Datei aus');
      return;
    }

    setIsLoading(true);
    setProgress(0);
    setError(null);
    setSuccess(false);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // 1. Upload starten - gibt sofort jobId zurück
      const uploadResponse = await fetch(`${BACKEND_URL}/api/cv-anonymizer-async`, {
        method: 'POST',
        body: formData,
        headers: {
          'x-api-key': import.meta.env.VITE_INTERNAL_API_KEY || '',
        },
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        throw new Error(errorData.error || 'Upload fehlgeschlagen');
      }

      const { jobId } = await uploadResponse.json();
      console.log(`[CV-Anonymizer] Job started: ${jobId}`);

      // 2. Simuliere Progress während Verarbeitung
      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (prev < 90) return prev + Math.random() * 10;
          return prev;
        });
      }, 500);

      // 3. Poll Status alle 2 Sekunden
      let attempts = 0;
      const maxAttempts = 60; // 2 Min timeout

      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 2000)); // Warte 2s
        
        const statusResponse = await fetch(`${BACKEND_URL}/api/cv-anonymizer-async/status/${jobId}`);
        const statusData = await statusResponse.json();
        
        console.log(`[CV-Anonymizer] Status check ${attempts + 1}: ${statusData.status}`);

        if (statusData.status === 'completed') {
          // 4. Download fertige PDF
          clearInterval(progressInterval);
          setProgress(100);

          const downloadResponse = await fetch(`${BACKEND_URL}/api/cv-anonymizer-async/download/${jobId}`);
          
          if (!downloadResponse.ok) {
            throw new Error('Download fehlgeschlagen');
          }

          const blob = await downloadResponse.blob();
          const url = window.URL.createObjectURL(blob);
          setDownloadLink(url);
          setSuccess(true);
          setFile(null);
          
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }

          setTimeout(() => {
            setIsLoading(false);
            setProgress(0);
          }, 2000);

          return;
        } else if (statusData.status === 'error') {
          clearInterval(progressInterval);
          throw new Error(statusData.message || 'Verarbeitung fehlgeschlagen');
        }

        attempts++;
      }

      // Timeout nach 2 Minuten
      clearInterval(progressInterval);
      throw new Error('Verarbeitung dauert zu lange. Bitte versuche es mit einer kleineren PDF.');

    } catch (err) {
      setError(err.message || '❌ Ein unerwarteter Fehler ist aufgetreten');
      setIsLoading(false);
      setProgress(0);
    }
  };

  const handleDownload = () => {
    if (downloadLink) {
      const a = document.createElement('a');
      a.href = downloadLink;
      a.download = `anonymisierter_lebenslauf_${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadLink);
      
      // Reset
      setSuccess(false);
      setDownloadLink(null);
      setProgress(0);
    }
  };

  return (
    <>
      <Helmet>
        <title>Lebenslauf Anonymisieren | CV Schwärzen | Kostenlos | Jobdaten</title>
        <meta name="description" content="Anonymisiere deinen Lebenslauf kostenlos mit KI. Schwärze automatisch Namen, Adressen & Kontaktdaten. Keine Registrierung nötig." />
        <meta name="keywords" content="lebenslauf anonymisieren, cv anonymisieren, bewerbung anonymisieren, lebenslauf schwärzen, cv schwärzen, anonymer lebenslauf, cv anonymisieren kostenlos, lebenslauf daten entfernen, bewerbung datenschutz, lebenslauf anonymisieren tool" />
        <meta name="author" content="Jobdaten" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://jobdaten.com/cv-anonymizer" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Lebenslauf Anonymisieren | CV Schwärzen | Kostenlos" />
        <meta property="og:description" content="Anonymisiere deinen Lebenslauf kostenlos mit KI. Schwärze automatisch Namen, Adressen & Kontaktdaten." />
        <meta property="og:url" content="https://jobdaten.com/cv-anonymizer" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Jobdaten" />
        <meta property="og:locale" content="de_DE" />
        <meta property="og:image" content="https://jobdaten.com/images/og-image.jpg" />
        
        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <div className="relative bg-white min-h-screen">
      <Navbar isHomePage={false} />

      {/* Hero Section */}
      <div className="relative isolate pt-14">
        {/* Background Overlays */}
        <div className="w-[600px] h-[600px] bg-[#FFD6D6] blur-[300px] rounded-full absolute top-0 right-[25%] -z-10"></div>
        <div className="w-[600px] h-[600px] bg-[#B588FF] blur-[400px] rounded-full absolute top-[25%] right-0 -z-10"></div>

        <div className="project-container md:mt-28 mt-16 md:mb-20 mb-10">
          <div className="flex flex-col items-center justify-center gap-10">
            {/* Header */}
            <div className="text-center">
              <h1 className="xl:text-[62px] xl:leading-[135%] md:text-5xl text-4xl font-bold text-black mb-6">
                Lebenslauf Anonymisierer
              </h1>
              <p className="md:text-lg text-base text-[#A6A6A6] max-w-3xl mx-auto">
                Schwärze automatisch sensible Daten in deinem Lebenslauf. 
                Nutze künstliche Intelligenz um Namen, Adressen, Kontaktdaten und mehr zu anonymisieren.
              </p>
            </div>

            {/* Upload Box - GRÖSSER mit Drag & Drop */}
            <div 
              className={`w-full max-w-4xl bg-white rounded-3xl shadow-2xl p-12 border-4 border-dashed transition-all duration-300 ${
                isDragging 
                  ? 'border-[#B588FF] bg-purple-50 scale-105' 
                  : 'border-[#E0E0E0] hover:border-[#B588FF]'
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              {/* File Input (hidden) */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                className="hidden"
                disabled={isLoading}
              />

              {/* Upload Area - GRÖSSER */}
              <div
                className="flex flex-col items-center justify-center cursor-pointer py-16"
                onClick={() => !isLoading && fileInputRef.current?.click()}
              >
                <svg
                  className={`w-32 h-32 mb-6 transition-all duration-300 ${
                    isDragging ? 'text-[#B588FF] scale-110' : 'text-[#B588FF]'
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                <h3 className="text-2xl font-bold text-[#191A15] mb-3">
                  {isDragging ? '📥 Datei hier ablegen...' : '📄 PDF hier hochladen'}
                </h3>
                <p className="text-lg text-[#A6A6A6] text-center mb-4">
                  Ziehe deine PDF-Datei hierher<br />
                  <span className="text-sm">oder klicke um eine Datei auszuwählen</span>
                </p>
                <p className="text-sm text-[#999] mt-2">
                  ⚠️ Maximale Dateigröße: 10 MB | Nur Text-PDFs (keine Scans)
                </p>
              </div>

              {/* Selected File Display - GRÖSSERER STIL */}
              {file && !isLoading && (
                <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-2xl flex items-center justify-between shadow-md">
                  <div className="flex items-center gap-4">
                    <div className="bg-green-500 rounded-full p-3">
                      <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-bold text-green-900 text-lg">{file.name}</p>
                      <p className="text-base text-green-700">
                        Größe: {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white rounded-full p-2 transition-all duration-200 hover:scale-110"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Error Message - DETAILLIERTER */}
              {error && (
                <div className="mt-8 p-6 bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-300 rounded-2xl shadow-md">
                  <div className="flex items-start gap-4">
                    <div className="bg-red-500 rounded-full p-2 flex-shrink-0">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-red-900 font-bold text-lg mb-2">Fehler aufgetreten</h4>
                      <p className="text-red-800 font-medium text-base leading-relaxed">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Loading Bar - GRÖSSER und ANIMIERTER */}
              {isLoading && (
                <div className="mt-8">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-xl font-bold text-[#191A15]">🔄 Anonymisierung läuft...</p>
                    <span className="text-2xl font-bold text-[#B588FF]">{Math.round(progress)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden shadow-inner">
                    <div
                      className="bg-gradient-to-r from-[#B588FF] via-[#9F6FFF] to-[#8B5FFF] h-full transition-all duration-300 ease-out rounded-full relative"
                      style={{ width: `${Math.min(progress, 100)}%` }}
                    >
                      <div className="absolute inset-0 bg-white/30 animate-pulse"></div>
                    </div>
                  </div>
                  <p className="text-base text-[#A6A6A6] mt-4 text-center">
                    ⏳ Bitte warten, während deine Datei mit KI verarbeitet wird...
                  </p>
                </div>
              )}

              {/* Success Message with Download Button - GRÖSSER */}
              {success && downloadLink && (
                <div className="mt-8 p-8 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-2xl text-center shadow-lg">
                  <div className="bg-green-500 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-green-900 mb-3">Erfolgreich anonymisiert!</h3>
                  <p className="text-green-800 mb-6">Dein Lebenslauf wurde erfolgreich anonymisiert</p>
                  <button
                    onClick={handleDownload}
                    className="w-full px-8 py-4 text-xl bg-gradient-to-r from-[#B588FF] to-[#8B5FFF] text-white font-bold rounded-full hover:shadow-2xl transition-all duration-300 hover:scale-105 active:scale-95"
                  >
                    📥 Anonymisierte PDF herunterladen
                  </button>
                </div>
              )}

              {/* Generate Button - GRÖSSER */}
              {!isLoading && !success && file && (
                <>
                  <button
                    onClick={handleGenerateClick}
                    className="w-full mt-8 px-10 py-5 text-xl bg-gradient-to-r from-[#B588FF] to-[#8B5FFF] text-white font-bold rounded-full hover:shadow-2xl transition-all duration-300 hover:scale-105 active:scale-95"
                  >
                    🔒 Jetzt Anonymisieren
                  </button>
                  <p className="text-center text-sm text-[#666] mt-4 leading-relaxed">
                    Durch das Hochladen einer Datei stimmen Sie unseren{' '}
                    <a 
                      href="/impressum" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#B588FF] hover:text-[#8B5FFF] underline font-medium"
                    >
                      Nutzungsbedingungen
                    </a>
                    {' '}und der{' '}
                    <a 
                      href="/datenschutz" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#B588FF] hover:text-[#8B5FFF] underline font-medium"
                    >
                      Datenschutzerklärung
                    </a>
                    {' '}zu.
                  </p>
                </>
              )}

              {!file && !isLoading && !success && (
                <>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full mt-8 px-10 py-5 text-xl bg-gradient-to-r from-[#B588FF] to-[#8B5FFF] text-white font-bold rounded-full hover:shadow-2xl transition-all duration-300 hover:scale-105 active:scale-95"
                  >
                    📁 PDF Datei auswählen
                  </button>
                  <p className="text-center text-sm text-[#666] mt-4 leading-relaxed">
                    Durch das Hochladen einer Datei stimmen Sie unseren{' '}
                    <a 
                      href="/impressum" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#B588FF] hover:text-[#8B5FFF] underline font-medium"
                    >
                      Nutzungsbedingungen
                    </a>
                    {' '}und der{' '}
                    <a 
                      href="/datenschutz" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#B588FF] hover:text-[#8B5FFF] underline font-medium"
                    >
                      Datenschutzerklärung
                    </a>
                    {' '}zu.
                  </p>
                </>
              )}
            </div>

            {/* Features - NEUE FEATURES */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mt-20 max-w-6xl w-full">
              <div className="text-center bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 border border-purple-100">
                <div className="mb-6 text-6xl">🤖</div>
                <h3 className="font-bold text-[#191A15] text-xl mb-3">KI-basierte Erkennung</h3>
                <p className="text-base text-[#A6A6A6] leading-relaxed">
                  Modernste Gemini AI erkennt automatisch Namen, Adressen, Telefonnummern, E-Mails und weitere sensible Informationen
                </p>
              </div>
              <div className="text-center bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 border border-purple-100">
                <div className="mb-6 text-6xl">⚡</div>
                <h3 className="font-bold text-[#191A15] text-xl mb-3">Blitzschnell</h3>
                <p className="text-base text-[#A6A6A6] leading-relaxed">
                  Dein Lebenslauf wird in Sekunden verarbeitet. Lade hoch, warte kurz, fertig - einfacher geht's nicht
                </p>
              </div>
              <div className="text-center bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 border border-purple-100">
                <div className="mb-6 text-6xl">🎯</div>
                <h3 className="font-bold text-[#191A15] text-xl mb-3">Präzise Schwärzung</h3>
                <p className="text-base text-[#A6A6A6] leading-relaxed">
                  Intelligente Schwärzung mit schwarzen Balken über sensiblen Daten - professionell und zuverlässig
                </p>
              </div>
            </div>

            {/* Zusätzliche Info Box */}
            <div className="mt-16 max-w-4xl w-full bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-center text-[#191A15] mb-4">
                💡 Wichtige Hinweise
              </h3>
              <div className="grid md:grid-cols-2 gap-6 text-left">
                <div className="flex items-start gap-3">
                  <span className="text-2xl flex-shrink-0">✅</span>
                  <div>
                    <h4 className="font-bold text-[#191A15] mb-1">Text-PDFs verwenden</h4>
                    <p className="text-sm text-[#666]">Nur PDFs mit echtem Text funktionieren (aus Word, Google Docs, etc.)</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-2xl flex-shrink-0">❌</span>
                  <div>
                    <h4 className="font-bold text-[#191A15] mb-1">Keine Scans oder Bilder</h4>
                    <p className="text-sm text-[#666]">Eingescannte PDFs oder Bild-PDFs enthalten keinen extrahierbaren Text</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-2xl flex-shrink-0">📏</span>
                  <div>
                    <h4 className="font-bold text-[#191A15] mb-1">Maximale Größe 10 MB</h4>
                    <p className="text-sm text-[#666]">Komprimiere große PDFs vor dem Upload</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-2xl flex-shrink-0">🎁</span>
                  <div>
                    <h4 className="font-bold text-[#191A15] mb-1">Kostenlos & Ohne Anmeldung</h4>
                    <p className="text-sm text-[#666]">Keine Registrierung erforderlich - einfach PDF hochladen und loslegen</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
    </>
  );
}

export default CVAnonymizer;